// Shared pastel UI primitives for Care Connections
import React from "react";
import { Star, X } from "lucide-react";

export const ccInput =
  "w-full bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl px-4 py-2.5 text-sm text-[#374151] placeholder:text-[#9CA3AF] outline-none focus:ring-2 focus:ring-[#B3E0F2] focus:border-transparent transition-all duration-200 dark:bg-[#273244] dark:border-[#374457] dark:text-slate-100";

export const ccLabel = "text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] dark:text-slate-400 block mb-1.5";

export const ccCard =
  "bg-white border border-[#FCE6E9] rounded-3xl shadow-sm shadow-pink-100/50 dark:bg-[#232D3D] dark:border-[#374457] dark:shadow-none";

export const ccBtnPink =
  "px-5 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200 whitespace-nowrap";

export const ccBtnBlue =
  "px-5 py-2.5 bg-[#B3E0F2] hover:bg-[#9ACCE0] text-[#374151] rounded-full text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-blue-200 whitespace-nowrap";

export const ccBtnGhost =
  "px-5 py-2.5 bg-[#FFF9F2] border border-[#FCE6E9] text-[#6B7280] rounded-full text-sm font-semibold hover:bg-[#FCE6E9] transition-all duration-200 dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300 whitespace-nowrap";

export const Stars = ({ rating, size = "w-4 h-4" }) => (
  <span className="inline-flex items-center gap-0.5" data-testid="stars">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`${size} ${i <= Math.round(rating) ? "text-[#F5C445] fill-[#F5C445]" : "text-[#E5E7EB] fill-[#E5E7EB]"}`}
      />
    ))}
  </span>
);

export const Avatar = ({ src, name, size = "w-11 h-11" }) => (
  <div className={`${size} rounded-full bg-white border-2 border-[#FCE6E9] overflow-hidden flex items-center justify-center shrink-0 shadow-sm dark:border-[#374457] dark:bg-[#1F2937]`}>
    {src ? (
      <img src={src} alt={name} className="w-full h-full object-cover" />
    ) : (
      <span className="text-sm font-black text-[#E5A0BC] font-heading">{(name || "?").charAt(0)}</span>
    )}
  </div>
);

export const EmptyState = ({ text, testId }) => (
  <div className="p-10 border-2 border-dashed border-[#FCE6E9] rounded-3xl text-center dark:border-[#374457]" data-testid={testId}>
    <p className="text-xs text-[#9CA3AF] font-medium">{text}</p>
  </div>
);

export const CCModal = ({ children, onClose, testId, wide = false }) => (
  <div
    className="fixed inset-0 z-50 bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in"
    data-testid={testId}
    onClick={onClose}
  >
    <div
      className={`bg-white border border-[#FCE6E9] rounded-[2rem] ${wide ? "max-w-2xl" : "max-w-lg"} w-full p-8 shadow-xl shadow-blue-900/5 animate-modal-in max-h-[90vh] overflow-y-auto dark:bg-[#232D3D] dark:border-[#374457]`}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
);

export const ModalHeader = ({ title, icon: Icon, onClose, closeTestId }) => (
  <div className="flex justify-between items-center mb-6 border-b border-[#FCE6E9] dark:border-[#374457] pb-4">
    <h2 className="text-lg font-extrabold text-[#374151] dark:text-slate-100 flex items-center gap-2 font-heading">
      {Icon && (
        <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#E5A0BC]" />
        </span>
      )}
      {title}
    </h2>
    <button
      onClick={onClose}
      data-testid={closeTestId}
      className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors dark:hover:bg-[#2f3c50]"
    >
      <X className="w-4 h-4" />
    </button>
  </div>
);

export const StatusPill = ({ status }) => {
  const map = {
    sent: ["bg-[#E6F2FA] text-[#3D7E9E] border-[#B3E0F2]", "Request Sent"],
    responded: ["bg-[#E6F2FA] text-[#3D7E9E] border-[#B3E0F2]", "Caregiver Responded"],
    connected: ["bg-[#E2F4E1] text-[#2F6031] border-[#C4E5C2]", "Connected"],
    confirmed: ["bg-[#E2F4E1] text-[#2F6031] border-[#C4E5C2]", "Arrangement Confirmed"],
    completed: ["bg-[#FFF2D1] text-[#7A5A17] border-[#F5E3B3]", "Completed"],
    reviewed: ["bg-[#FCE6E9] text-[#B2607F] border-[#F8C8DC]", "Reviewed"],
    declined: ["bg-rose-50 text-rose-500 border-rose-200", "Declined"],
  };
  const [cls, text] = map[status] || map.sent;
  return (
    <span className={`px-3 py-1 border text-[10px] font-extrabold uppercase tracking-wide rounded-full ${cls}`} data-testid={`status-pill-${status}`}>
      {text}
    </span>
  );
};
